import React, { FC, Dispatch, SetStateAction } from 'react';
import {
  Box,
  StarsRatingBar,
  FormField,
  Text,
  Tooltip,
} from '@wix/design-system';
import { StatusAlertFilled } from '@wix/wix-ui-icons-common';
import { StarsRatingBarProps } from '@wix/design-system/dist/types/StarsRatingBar';
import { StatusIndicatorState } from '@wix/design-system/dist/types/StatusIndicator';
import { useTranslation } from '@wix/fe-essentials-standalone';
import s from './StarRating.module.scss';

export type StarRatingProps = {
  overallScore: StarsRatingBarProps['value'];
  reviewScoreStatus: StatusIndicatorState | undefined;
  isReviewScoreChanged: boolean;
  setReviewScore: (score: StarsRatingBarProps['value']) => void;
  setReviewScoreError: Dispatch<
    SetStateAction<StatusIndicatorState | undefined>
  >;
  setIsReviewScoreChanged: Dispatch<SetStateAction<boolean>>;
  onRateChange?: (rate: number) => void;
};

const StarRating: FC<StarRatingProps> = ({
  overallScore,
  reviewScoreStatus,
  isReviewScoreChanged,
  setReviewScore,
  setReviewScoreError,
  setIsReviewScoreChanged,
  onRateChange,
}) => {
  const { t } = useTranslation();

  const starRatingDescriptionValues: StarsRatingBarProps['descriptionValues'] =
    [
      t('client-dashboard-review-modal-stars-rating-description-value-1'),
      t('client-dashboard-review-modal-stars-rating-description-value-2'),
      t('client-dashboard-review-modal-stars-rating-description-value-3'),
      t('client-dashboard-review-modal-stars-rating-description-value-4'),
      t('client-dashboard-review-modal-stars-rating-description-value-5'),
    ];

  return (
    <FormField
      required
      label={t('client-dashboard-review-modal-stars-rating-label')}
    >
      <Box
        verticalAlign="middle"
        className={reviewScoreStatus === 'error' ? s.StarRatingError : ''}
      >
        <div
          onMouseEnter={() => {
            setReviewScoreError(undefined);
          }}
          onMouseLeave={() => {
            isReviewScoreChanged &&
              !overallScore &&
              setReviewScoreError('error');
          }}
        >
          <StarsRatingBar
            dataHook="review-modal-stars-rating"
            value={overallScore}
            onChange={(rate: number) => {
              setReviewScore(rate as StarsRatingBarProps['value']);
              setIsReviewScoreChanged(true);
              setReviewScoreError(undefined);
              onRateChange && onRateChange(rate);
            }}
            descriptionValues={starRatingDescriptionValues}
          />
        </div>
        {reviewScoreStatus === 'error' && (
          <Box marginBottom="-4px" marginLeft="-6px">
            <Tooltip
              content={t('marketplace.template-page.review-modal.rating-error')}
              maxWidth="300px"
            >
              <Text dataHook="review-modal-stars-rating-error" skin="error">
                <StatusAlertFilled />
              </Text>
            </Tooltip>
          </Box>
        )}
      </Box>
    </FormField>
  );
};

export default StarRating;
